// Utilities for cleaning up Stripe invoice line `description` strings.
// Stripe emits verbose, locale-specific phrasing like:
//   "Unused time on 2 × Accounts after May 24, 2026 (prorated)"
//   "Remaining time on Accounts Premium after 24 May 2026"
// These helpers normalize them into compact labels suitable for tight UIs.

const STRIPE_DESC_PATTERNS = {
  prorationTail: /\s*\(prorated\)\s*$/i,
  // "after May 24, 2026" / "after May 24 2026"
  dateTailEN: /\s+after\s+[A-Za-z]+\s+\d+,?\s*\d{4}\s*$/i,
  // "after 24 May 2026"
  dateTailNonEN: /\s+after\s+\d+\s+[A-Za-z]+\s+\d{4}\s*$/i,
  unusedPrefix: /^Unused time on\s+/i,
  remainingPrefix: /^Remaining time on\s+/i,
  // Stripe graduated-tier pricing emits two lines per item:
  //   "1 × Dedicated Environment (Tier 1 at $30.00 / month)"  ← base env
  //   "3 × Dedicated Environment (Tier 2 at $10.00 / month)"  ← per-extra-acct
  // The "(Tier N at $X / cycle)" suffix is Stripe boilerplate that leaks
  // tier mechanics to the user. Strip it; rewrite tier roles inline.
  tierSuffix: /\s*\(Tier\s+(\d+)\s+at\s+[^)]*\)\s*$/i,
  // Untiered rate suffix, e.g. the flat futures env: "(at $25.00 / month)".
  rateSuffix: /\s*\(at\s+[^)]*\)\s*$/i
};

// Separates a product from its quantity detail: "Dedicated Environment · 1 env".
const DETAIL_SEPARATOR = ' · ';

const FALLBACK_LABEL = 'Line item';

// Map Stripe's tier index to a friendly role. Tier 1 = base bundle item,
// Tier 2 = per-extra-unit overage. Hardcoded to our DE pricing model.
const TIER_ROLE = {
  1: 'base',
  2: 'extra'
};

/**
 * Strip verbose Stripe tails ("after May 24, 2026", "(prorated)") and the
 * "Unused time on" / "Remaining time on" prefixes so the description fits
 * a compact summary row. Also collapses graduated-tier suffixes so DE
 * (and other tiered SKUs) read as "Product (base)" / "Product (extra)"
 * instead of leaking "(Tier 1 at $30 / month)" mechanics.
 *
 * @param {string | null | undefined} raw
 * @returns {string}
 */
export function shortenStripeDescription(raw) {
  if (!raw) return FALLBACK_LABEL;
  let tier = 0;
  let cleaned = String(raw)
    .replace(STRIPE_DESC_PATTERNS.prorationTail, '')
    .replace(STRIPE_DESC_PATTERNS.dateTailEN, '')
    .replace(STRIPE_DESC_PATTERNS.dateTailNonEN, '')
    .replace(STRIPE_DESC_PATTERNS.unusedPrefix, '')
    .replace(STRIPE_DESC_PATTERNS.remainingPrefix, '')
    .replace(STRIPE_DESC_PATTERNS.tierSuffix, (_m, t) => {
      tier = Number(t);
      return '';
    })
    .replace(STRIPE_DESC_PATTERNS.rateSuffix, '')
    .trim();
  if (!cleaned) return FALLBACK_LABEL;

  // DE-specific rewrite: Stripe emits "N × Dedicated Environment" once per tier
  // (base + per-extra-acct). Quantity reads as "envs" on tier 1 and "extra
  // accounts" on tier 2 — surface that semantic instead of the raw "× N".
  // A futures env is a flat price with no tiers, so it arrives untiered and is
  // counted in envs too.
  const deMatch = cleaned.match(/^(\d+)\s*×\s*Dedicated Environment\b(.*)$/i);
  if (deMatch) {
    const qty = Number(deMatch[1]);
    const trailing = (deMatch[2] || '').trim();
    const unit = tier === 2 ? (qty === 1 ? 'extra acct' : 'extra accts') : qty === 1 ? 'env' : 'envs';
    return `Dedicated Environment${DETAIL_SEPARATOR}${qty} ${unit}${trailing ? ` ${trailing}` : ''}`;
  }

  // Non-DE tier line: append friendly tier role suffix so the user still sees
  // base vs overage without the dollar/cycle mechanics.
  if (tier > 0) {
    const role = TIER_ROLE[tier] || `tier ${tier}`;
    return `${cleaned} (${role})`;
  }

  return cleaned;
}

/**
 * Stripe stamps a discounted line's description with "(with N% off)" and
 * reports the line `amount` already net of that discount.
 *
 * @type {RegExp}
 */
const OFF_MARKER_RE = /\(with ([\d.]+)% off\)/i;

/**
 * True when any CHARGE line already carries its discount inside its amount.
 *
 * Item-level discounts (modify/add carts) are netted into the line by Stripe;
 * invoice-level ones (plain creates) are not. Negative lines are ignored — an
 * unused-time credit carries the marker whenever the PREVIOUS purchase was
 * discounted, which says nothing about this cart.
 *
 * @param {{ linesList?: Array<{ amount?: number, description?: string }> } | null | undefined} preview
 * @returns {boolean}
 */
export function chargeLinesAreNet(preview) {
  const lines = (preview && preview.linesList) || [];
  return lines.some((l) => (Number(l && l.amount) || 0) > 0 && OFF_MARKER_RE.test((l && l.description) || ''));
}

/**
 * True when a CREDIT line reverses seats that were bought under a discount —
 * Stripe refunds them at the rate actually paid, so a cart with no discount of
 * its own re-bills those same seats at the standard rate. Drives the
 * "your earlier discount doesn't carry over" warning.
 *
 * @param {{ linesList?: Array<{ amount?: number, description?: string }> } | null | undefined} preview
 * @returns {boolean}
 */
export function creditLinesCarryDiscount(preview) {
  const lines = (preview && preview.linesList) || [];
  return lines.some((l) => (Number(l && l.amount) || 0) < 0 && OFF_MARKER_RE.test((l && l.description) || ''));
}

/**
 * Preview lines prepared for display, so the summary reads as one arithmetic:
 * lines → Subtotal → Discount → Due today.
 *
 * Stripe reports item-level discounts already netted into the charge line
 * ("9 × Accounts (with 30.0% off) $62.97") while the preview's `subtotal` is
 * the GROSS figure the "Discount" row subtracts from. Rendering both verbatim
 * put a $62.97 line under a $40.98 subtotal — visibly not adding up. Here the
 * marked charge lines are grossed back up (the row below states the discount,
 * so the marker is dropped from the label) and credits are reworded. The
 * discount is spread across marked lines in proportion to their net amounts,
 * with the rounding remainder going to the largest line so the displayed
 * amounts sum to `subtotal` exactly.
 *
 * Lines without a marker are untouched: an invoice-level coupon (plain create)
 * already reports gross lines, and a price-restricted code leaves excluded
 * products unmarked.
 *
 * @param {{ linesList?: Array<{ amount?: number, description?: string }>, discount?: number } | null | undefined} preview
 * @returns {Array<{ amount: number, description: string }>}
 */
// A credit's marker, relabelled: what the reversed seats were bought at, not a discount on this cart. The
// pattern finds it again so the summary can colour it with the "doesn't carry over" warning.
const ORIGINAL_DISCOUNT_LABEL = '(originally $1% off)';
const ORIGINAL_DISCOUNT_RE = /\(originally [\d.]+% off\)/i;

export function toDisplayLines(preview) {
  const lines = ((preview && preview.linesList) || []).map((l) => ({
    amount: Number(l && l.amount) || 0,
    description: (l && l.description) || ''
  }));

  const relabelled = lines.map((l) =>
    l.amount < 0 ? { ...l, description: l.description.replace(OFF_MARKER_RE, ORIGINAL_DISCOUNT_LABEL) } : l
  );

  const discount = Math.max(0, Number(preview && preview.discount) || 0);
  const marked = relabelled.filter((l) => l.amount > 0 && OFF_MARKER_RE.test(l.description));
  const netSum = marked.reduce((sum, l) => sum + l.amount, 0);
  if (!discount || !marked.length || netSum <= 0) return relabelled;

  // Proportional split, remainder to the largest line — keeps the sum exact.
  const shares = new Map();
  let allocated = 0;
  marked.forEach((l) => {
    const share = Math.round((discount * l.amount) / netSum);
    shares.set(l, share);
    allocated += share;
  });
  const largest = marked.reduce((a, b) => (b.amount > a.amount ? b : a));
  shares.set(largest, shares.get(largest) + (discount - allocated));

  return relabelled.map((l) =>
    shares.has(l)
      ? {
          amount: l.amount + shares.get(l),
          description: l.description
            .replace(OFF_MARKER_RE, '')
            .replace(/\s{2,}/g, ' ')
            .trim()
        }
      : l
  );
}

export { STRIPE_DESC_PATTERNS, FALLBACK_LABEL, OFF_MARKER_RE, ORIGINAL_DISCOUNT_RE };

/**
 * Shorten every invoice-preview description, then collapse the pair Stripe emits
 * for a flat-fee tiered price — a $0 per-unit line carrying the quantity plus a
 * flat line carrying the money (the futures dedicated env) — into one row that
 * keeps the richer label and the amount actually charged. Groups with more than
 * one paying line (prorations, CFD base + extra accounts) are left untouched.
 *
 * @param {{ description?: string, amount?: number }[]} lines
 * @returns {{ label: string, amount: number }[]}
 */
export function summarizeInvoiceLines(lines) {
  const rows = (lines || []).map((l) => ({ label: shortenStripeDescription(l.description), amount: Number(l.amount) || 0 }));
  const byProduct = new Map();
  rows.forEach((row) => {
    const key = row.label.split(DETAIL_SEPARATOR)[0].trim();
    byProduct.set(key, [...(byProduct.get(key) || []), row]);
  });
  const dropped = new Set();
  byProduct.forEach((group) => {
    if (group.length < 2) return;
    const paying = group.filter((r) => r.amount !== 0);
    if (paying.length !== 1) return;
    // The $0 line is the one that names the quantity, so keep its wording.
    paying[0].label = group.reduce((a, b) => (b.label.length > a.label.length ? b : a)).label;
    group.forEach((r) => r !== paying[0] && dropped.add(r));
  });
  return rows.filter((r) => !dropped.has(r));
}
